/**
 * URL search parameters hook using nuqs.
 *
 * Provides type-safe URL state management for PLP filters.
 * Uses nuqs for automatic URL synchronization.
 *
 * ## Filter Logic:
 *
 * Currently ALL filters use OR logic (comma separator):
 * - `?collection=Navitimer,Superocean` → Show watches in Navitimer OR Superocean
 * - `?type=Limited,Gold` → Show watches that are Limited OR Gold
 *
 * ## Future AND Support:
 *
 * AND logic infrastructure (plus separator) exists but is unused.
 * To enable: change parseAsOrArray to parseAsAndArray for the filter.
 */

'use client';

import {
  createParser,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';
import { useCallback, useMemo } from 'react';
import { AND_SEPARATOR, OR_SEPARATOR } from '../params';
import type { FilterState, SortOption } from '../types';

// =============================================================================
// Custom Array Parsers with Different Separators
// =============================================================================

/**
 * Create an array parser with a custom separator.
 *
 * @param separator - The character to use for joining/splitting values
 */
function createArrayParser(separator: string) {
  return createParser({
    parse: (value: string) => {
      if (!value) return null;
      const arr = value
        .split(separator)
        .map((s) => s.trim())
        .filter(Boolean);
      return arr.length > 0 ? arr : null;
    },
    serialize: (value: string[] | null) => {
      if (!value || value.length === 0) return '';
      return value.join(separator);
    },
  });
}

/** Parser for OR filters (comma-separated) - used by collection */
const parseAsOrArray = createArrayParser(OR_SEPARATOR);

/** Parser for AND filters (plus-separated) - used by type, case, dial-color, size */
const _parseAsAndArray = createArrayParser(AND_SEPARATOR);

// =============================================================================
// Parser Definitions
// =============================================================================

const sortOptions = ['relevance', 'price_asc', 'price_desc', 'newest'] as const;

/**
 * URL parameter parsers for nuqs.
 *
 * Currently ALL filters use OR logic (comma separator): `?type=A,B` means A OR B
 *
 * AND logic infrastructure (plus separator) is available but unused.
 * To enable AND for a filter, change parseAsOrArray to parseAsAndArray.
 */
export const searchParamsParsers = {
  /** Search query */
  q: parseAsString,

  /** SKU/Reference filter */
  sku: parseAsString,

  // ============================================
  // Watch filters
  // ============================================

  /** Collection filter - OR logic (comma-separated) */
  collection: parseAsOrArray,

  /** Size filter - OR logic (comma-separated) */
  size: parseAsOrArray,

  /** Case filter - OR logic (comma-separated) */
  case: parseAsOrArray,

  /** Dial color filter - OR logic (comma-separated) */
  'dial-color': parseAsOrArray,

  /** Type filter - OR logic (comma-separated) */
  type: parseAsOrArray,

  // ============================================
  // Watch bracelet category filters (Gallet)
  // ============================================

  /** Bracelet material category filter - OR logic (comma-separated) */
  'bracelet-material': parseAsOrArray,

  /** Bracelet color category filter - OR logic (comma-separated) */
  'bracelet-color': parseAsOrArray,

  // ============================================
  // Strap filters
  // ============================================

  /** Strap material filter - OR logic (comma-separated) */
  material: parseAsOrArray,

  /** Strap/band color filter - OR logic (comma-separated) */
  color: parseAsOrArray,

  /** Lug width filter - OR logic (comma-separated) */
  lug: parseAsOrArray,

  /** Lug type filter - OR logic (comma-separated) */
  'lug-type': parseAsOrArray,

  /** Buckle filter - OR logic (comma-separated) */
  buckle: parseAsOrArray,

  // ============================================
  // Eyewear filters
  // ============================================

  /** Frame material filter - OR logic (comma-separated) */
  'frame-material': parseAsOrArray,

  /** Lens color filter - OR logic (comma-separated) */
  'lens-color': parseAsOrArray,

  // ============================================
  // Store filters
  // ============================================

  /** Store type filter - OR logic (comma-separated) */
  'store-type': parseAsOrArray,

  // ============================================
  // Common filters
  // ============================================

  /** Minimum price */
  'price-min': parseAsInteger,

  /** Maximum price */
  'price-max': parseAsInteger,

  /**
   * Current page (1-indexed, defaults to 1).
   * Default clears `page` from the URL when it is 1 so listing URLs stay clean.
   */
  page: parseAsInteger.withDefault(1),

  /** Sort order (defaults to relevance) */
  sort: parseAsStringLiteral(sortOptions).withDefault('relevance'),
};

export type SearchParamsState = {
  [K in keyof typeof searchParamsParsers]: ReturnType<
    (typeof searchParamsParsers)[K]['parseServerSide']
  >;
};

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook to manage search URL parameters.
 *
 * Uses nuqs for type-safe URL state management with automatic
 * serialization and shallow routing.
 *
 * @example
 * const { params, setFilter, clearFilters, toFilterState } = useSearchParams();
 *
 * // Read current filters
 * console.log(params.collection); // ['navitimer']
 *
 * // Update a filter (resets page to 1)
 * setFilter('collection', ['navitimer', 'superocean']);
 *
 * // Convert to FilterState for search
 * const filters = toFilterState();
 */
export function useSearchParams() {
  const [params, setParams] = useQueryStates(searchParamsParsers, {
    // Full page reload to re-run RSC search
    shallow: false,
  });

  /**
   * Convert URL params to FilterState for the search function.
   */
  const toFilterState = useCallback((): FilterState => {
    return {
      query: params.q || undefined,
      sku: params.sku || undefined,
      // Watch filters
      collection: params.collection || undefined,
      size: params.size || undefined,
      case: params.case || undefined,
      dialColor: params['dial-color'] || undefined,
      type: params.type || undefined,
      // Watch bracelet category filters
      braceletMaterial: params['bracelet-material'] || undefined,
      braceletColor: params['bracelet-color'] || undefined,
      // Strap filters
      material: params.material || undefined,
      color: params.color || undefined,
      lug: params.lug || undefined,
      lugType: params['lug-type'] || undefined,
      buckle: params.buckle || undefined,
      // Eyewear filters
      frameMaterial: params['frame-material'] || undefined,
      lensColor: params['lens-color'] || undefined,
      // Common filters
      priceMin: params['price-min'] || undefined,
      priceMax: params['price-max'] || undefined,
      page: params.page,
      sortBy: params.sort as SortOption,
    };
  }, [params]);

  /**
   * Update a single filter and reset page to 1.
   */
  const setFilter = useCallback(
    <K extends keyof typeof params>(key: K, value: (typeof params)[K]) => {
      setParams({ [key]: value, page: 1 } as Partial<typeof params>);
    },
    [setParams]
  );

  /**
   * All supported filter keys for toggle operations.
   */
  type FilterKey =
    // Watch filters
    | 'collection'
    | 'size'
    | 'case'
    | 'dial-color'
    | 'type'
    // Watch bracelet category filters
    | 'bracelet-material'
    | 'bracelet-color'
    // Strap filters
    | 'material'
    | 'color'
    | 'lug'
    | 'lug-type'
    | 'buckle'
    // Eyewear filters
    | 'frame-material'
    | 'lens-color'
    // Store filters
    | 'store-type';

  /**
   * Toggle a value in a multi-select filter.
   */
  const toggleFilterValue = useCallback(
    (key: FilterKey, value: string) => {
      const current = params[key] || [];
      const newValue = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];

      setParams({ [key]: newValue.length > 0 ? newValue : null, page: 1 });
    },
    [params, setParams]
  );

  /**
   * Clear all filters and reset to defaults.
   */
  const clearFilters = useCallback(() => {
    setParams({
      q: null,
      sku: null,
      // Watch filters
      collection: null,
      size: null,
      case: null,
      'dial-color': null,
      type: null,
      // Watch bracelet category filters
      'bracelet-material': null,
      'bracelet-color': null,
      // Strap filters
      material: null,
      color: null,
      lug: null,
      'lug-type': null,
      buckle: null,
      // Eyewear filters
      'frame-material': null,
      'lens-color': null,
      // Store filters
      'store-type': null,
      // Common filters
      'price-min': null,
      'price-max': null,
      page: 1,
      sort: 'relevance',
    });
  }, [setParams]);

  /**
   * Check if any filters are active.
   */
  const hasActiveFilters = useMemo(() => {
    return !!(
      params.q ||
      params.sku ||
      // Watch filters
      params.collection?.length ||
      params.size?.length ||
      params.case?.length ||
      params['dial-color']?.length ||
      params.type?.length ||
      // Watch bracelet category filters
      params['bracelet-material']?.length ||
      params['bracelet-color']?.length ||
      // Strap filters
      params.material?.length ||
      params.color?.length ||
      params.lug?.length ||
      params['lug-type']?.length ||
      params.buckle?.length ||
      // Eyewear filters
      params['frame-material']?.length ||
      params['lens-color']?.length ||
      // Store filters
      params['store-type']?.length ||
      // Price
      params['price-min'] ||
      params['price-max']
    );
  }, [params]);

  /**
   * Count of active filter groups.
   */
  const activeFilterCount = useMemo(() => {
    let count = 0;
    // Watch filters
    if (params.collection?.length) count++;
    if (params.size?.length) count++;
    if (params.case?.length) count++;
    if (params['dial-color']?.length) count++;
    if (params.type?.length) count++;
    // Watch bracelet category filters
    if (params['bracelet-material']?.length) count++;
    if (params['bracelet-color']?.length) count++;
    // Strap filters
    if (params.material?.length) count++;
    if (params.color?.length) count++;
    if (params.lug?.length) count++;
    if (params['lug-type']?.length) count++;
    if (params.buckle?.length) count++;
    // Eyewear filters
    if (params['frame-material']?.length) count++;
    if (params['lens-color']?.length) count++;
    // Store filters
    if (params['store-type']?.length) count++;
    // Price
    if (params['price-min'] || params['price-max']) count++;
    return count;
  }, [params]);

  return {
    /** Current parameter values */
    params,

    /** Set multiple parameters at once */
    setParams,

    /** Set a single filter (resets page) */
    setFilter,

    /** Toggle a value in a multi-select filter */
    toggleFilterValue,

    /** Clear all filters */
    clearFilters,

    /** Convert to FilterState */
    toFilterState,

    /** Whether any filters are active */
    hasActiveFilters,

    /** Number of active filter groups */
    activeFilterCount,

    /** Current page number */
    page: params.page,

    /** Set page number */
    setPage: (page: number) => setParams({ page }),

    /** Current sort option */
    sort: params.sort,

    /** Set sort option (resets page) */
    setSort: (sort: SortOption) => setParams({ sort, page: 1 }),

    /** Current search query */
    query: params.q,

    /** Set search query (resets page) */
    setQuery: (q: string | null) => setParams({ q, page: 1 }),
  };
}

// Re-export server-compatible parseSearchParams for convenience
export { parseSearchParams } from '../params';
